

import { Link } from "react-router-dom";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import ServiceSchema from "../ServiceSchema";
import HeroSection from "./HeroSection";          // Same folder
import prefabBuilding from "../assets/pre-engineered.png"; // Adjust path if needed

const PrefabricatedBuildings = () => {
  const applications = [
    "Mobile Offices",
    "Site Cabins",
    "Security Guard Rooms",
    "Farm Houses & Sheds",
    "Godowns & Storage Units",
    "Commercial Shops",
    "Temporary or Semi-Permanent Structures",
  ];

  const features = [
    {
      title: "Quick Setup",
      description: "Installed in a fraction of the time compared to traditional buildings.",
    },
    {
      title: "Lightweight Yet Strong",
      description: "FRP + steel combination ensures durability without heavy foundations.",
    },
    {
      title: "Thermal & Weather Resistant",
      description: "Suitable for all climates with smart ventilation.",
    },
    {
      title: "Cost-Efficient",
      description: "Ideal for businesses seeking fast and budget-friendly solutions.",
    },
    {
      title: "Portable & Reusable",
      description: "Can be relocated whenever required.",
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Prefab Buildings & Steel Structures | Vasavi Structures"
        description="Vasavi Structures designs and delivers prefab buildings and steel structures in Hyderabad, including mobile offices, site cabins, farm sheds and godowns."
        keywords="prefab buildings Hyderabad, steel prefab buildings, mobile offices, site cabins, security rooms, farm sheds, godowns"
      />
      <ServiceSchema
        serviceType="Prefabricated Building Design & Fabrication"
        description="Design, fabrication and delivery of prefabricated buildings including mobile offices, site cabins, security rooms, farm sheds, godowns and commercial spaces."
      />

      <main>
        <HeroSection
          title="Prefabricated Buildings"
          subtitle="Smart, Quick & Cost-Effective"
          description="High-quality FRP and steel prefabricated buildings designed for versatile applications with speed, affordability, and portability."
          backgroundImage={prefabBuilding}
          primaryButtonText="Get Quote"
          primaryButtonLink="/enquiry"
          secondaryButtonText="Contact Us"
          secondaryButtonLink="/contact"
        />

        {/* Main Content */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="animate-slide-in-left">
                <img
                  src={prefabBuilding}
                  alt="Prefabricated Building"
                  className="rounded-xl shadow-2xl w-full object-cover"
                />
              </div>

              <div className="animate-slide-in-right">
                <h2 className="font-montserrat text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Fast Installation. <span className="text-secondary">Long-Term Durability.</span>
                </h2>
                <p className="text-muted-foreground mb-4">
                  Our prefab buildings are pre-manufactured at our facility and transported to your site for quick assembly. This reduces project time, eliminates heavy civil construction, and provides a ready-to-use building within days.
                </p>
                <p className="text-muted-foreground mb-6">
                  We offer multiple design choices, including office cabins, storage units, godowns, shops, portable cabins, site offices, farm structures, and more.
                </p>

                <h3 className="font-montserrat text-xl font-bold text-foreground mb-4">
                  Applications:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {applications.map((app) => (
                    <div key={app} className="flex items-center gap-2 text-muted-foreground">
                      <svg
                        className="w-5 h-5 text-secondary flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {app}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="section-padding bg-muted">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-montserrat text-3xl md:text-4xl font-bold text-foreground mb-4">
                Why Choose Our <span className="text-primary">Prefab Buildings?</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={`p-6 bg-card rounded-xl shadow-lg card-hover animate-slide-up stagger-${index + 1}`}
                  style={{ animationFillMode: "both" }}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="font-montserrat text-xl font-bold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-secondary">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-3xl md:text-4xl font-bold text-secondary-foreground mb-4">
              Smart Buildings for Modern Needs
            </h2>
            <p className="text-secondary-foreground/90 text-lg mb-8 max-w-2xl mx-auto">
              With flexible design, long-lasting materials, and exceptional build quality, Vasavi Structures is one of the leading prefabricated building manufacturers in Hyderabad.
            </p>
            <Link
              to="/enquiry"
              className="btn-primary inline-block px-8 py-4 text-lg font-medium hover:bg-primary/90 transition"
            >
              Request a Quote
            </Link>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default PrefabricatedBuildings;